import { CopyIcon, Pencil, TrashIcon } from 'assets/icons';
import Button from 'components/button';
import SlideOverBody from 'components/SlideOver/component/body';
import SlideOverFooter from 'components/SlideOver/component/footer';
import SlideOverHeader from 'components/SlideOver/component/header';
import useSlideOver from 'components/SlideOver/useSlideOver';
import useModal from 'components/modal/useModal';
import { dateToEpoch } from 'helper/helper';
import UseContext from 'hooks/useContext';
import { useEffect, useState } from 'react';
import useSlideOverBaseline from 'components/SlideOver/useSlideOverBaseline';
import { toast } from 'react-toastify';
import ApiDataBase from 'server';
import SlideOverAddMissionPlanningInvitationAttributionNotification from './slideOverAddMissionInvitationAttributionNotification/slideOverAddMissionInvitationAttributionNotification';
import SlideOverAddMissionMotif from './slideOverAddMissionMotif/slideOverAddMissionMotif';
import SlideOverAddMissionPlanning from './slideOverAddMissionPlanning/slideOverAddMissionPlanning';
import TemplateSelectStep from './missionTemplate/templateSelectStep';
import TemplateScreenTitle from './missionTemplate/templateScreenTitle';
import TemplateForm from './missionTemplate/templateForm';
import TemplateStepBadges from './missionTemplate/templateStepBadges';
import {
  EMPTY_TEMPLATE_FORM,
  TEMPLATE_STEPS,
  buildTemplatePayload,
  TEMPLATE_NAME_TAKEN_API_MESSAGE,
  TEMPLATE_NAME_TAKEN_MESSAGE,
  findTemplateFormError,
  findTemplateStepError,
  hasPendingTasks,
  templateToFormData,
} from './missionTemplate/templateFormData';
import TemplateMissionDetailsStep from './missionTemplate/templateMissionDetailsStep';
import TemplateMissionConfirmation from './missionTemplate/templateMissionConfirmation';
import {
  buildMissionDataFromTemplate,
  buildTemplateMissionPayload,
  withMissionCoefficient,
} from './missionTemplate/templateMissionData';
import { isSuperAdmin } from 'helper/roles';
import useMissionCoefficientField from './missionTemplate/useMissionCoefficientField';
import TemplateDetail from './missionTemplate/templateDetail';
import { getMissionCompanyErrorMessage } from './missionCompanyError';
import { getTemplateTitle } from './missionTemplate/missionTemplateFormat';
import { useRiskyPositions } from './missionTemplate/missionTemplateRiskyPositions';
import { templateText } from './missionTemplate/templateTexts';
import { INITIAL_CREATE_MISSION_DATA } from 'components/SlideOver/slideOverUnsavedChanges';
import useMissionPerimeterField from 'components/perimeter/useMissionPerimeterField';
import {
  getMissionPerimeterErrorMessage,
  withMissionPerimeter,
} from 'components/perimeter/missionPerimeter';

const slideOverAddMission = () => {
  const { toggleSlideOver } = useSlideOver();
  const [isLoadingAddMission, setIsLoadingAddMission] = useState(false);
  const [page, setPage] = useState(1);
  // A mission starts from a job description: pick the company and its template,
  // read it, then fill what the mission alone carries.
  const [screen, setScreen] = useState('select');
  const [openedTemplate, setOpenedTemplate] = useState(null);
  const [isDeletingTemplate, setIsDeletingTemplate] = useState(false);
  const [templateForm, setTemplateForm] = useState(EMPTY_TEMPLATE_FORM);
  const [templateStepKey, setTemplateStepKey] = useState(TEMPLATE_STEPS[0].key);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  // A save asked while added tasks are still being checked: it leaves on its
  // own once the last check has answered.
  const [isWaitingForTaskChecks, setIsWaitingForTaskChecks] = useState(false);
  // Loaded by the select screen: the names already taken in this company.
  const [companyTemplates, setCompanyTemplates] = useState([]);
  const riskyPositions = useRiskyPositions();
  const {
    dataCreateMission,
    setDataCreateMission,
    setSlideOverHasUnsavedChanges,
    setPendingDuplicateMissionTemplate,
    duplicatedMissionTemplateId,
    setDuplicatedMissionTemplateId,
    language,
    userData,
  } = UseContext();
  const perimeterField = useMissionPerimeterField(dataCreateMission.company);
  const coefficientField = useMissionCoefficientField(
    dataCreateMission.company,
    dataCreateMission.salary
  );
  const { toggle: toggleModal } = useModal();
  const token = localStorage.getItem('xsrfToken');

  useSlideOverBaseline({
    getSnapshot: () => dataCreateMission,
    deps: [dataCreateMission],
    setSlideOverHasUnsavedChanges,
  });

  // « Général » is gone: the job description carries the job itself. Steps go
  // by key, so adding one never shifts a number somewhere else.
  const MISSION_STEPS = [
    { key: 'motif', label: 'Motif' },
    { key: 'period', label: 'Période' },
    { key: 'details', label: 'Détails' },
    { key: 'invitation', label: 'Inviter / Notification' },
    { key: 'confirmation', label: 'Confirmation' },
  ];
  const stepKey = MISSION_STEPS[page - 1]?.key;
  const goToStep = (key) =>
    setPage(MISSION_STEPS.findIndex((step) => step.key === key) + 1);
  const isLastStep = stepKey === 'confirmation';

  const isNotEmptyObject = (obj) =>
    obj && typeof obj === 'object' && Object.keys(obj).length > 0;

  const isMotifStepValid = Boolean(
    dataCreateMission.motif && Object.keys(dataCreateMission.motif).length > 0
  );
  const isPlanningStepValid = Boolean(
    dataCreateMission.dateStart &&
      dataCreateMission.dateEnd &&
      dateToEpoch(new Date(dataCreateMission.dateEnd)) >=
        dateToEpoch(new Date(dataCreateMission.dateStart))
  );

  const canContinue =
    stepKey === 'motif'
      ? isMotifStepValid
      : stepKey === 'period'
      ? isPlanningStepValid
      : true;

  const closeSlideOver = () => {
    toggleSlideOver();
    setScreen('select');
    setOpenedTemplate(null);
    setPage(1);
    setDataCreateMission({ ...INITIAL_CREATE_MISSION_DATA });
    coefficientField.reset();
  };

  const submitAddMission = () => {
    if (coefficientField.isInvalid) return;
    setIsLoadingAddMission(true);
    const payload = withMissionCoefficient(
      withMissionPerimeter(
        buildTemplateMissionPayload(dataCreateMission),
        perimeterField
      ),
      coefficientField.override,
      isSuperAdmin(userData)
    );
    ApiDataBase.createMission(
      {
        token,
        ...payload,
      },
      (created) => {
        setIsLoadingAddMission(false);
        toast.success('La mission a bien été crée.');
        closeSlideOver();
      },
      (err) => {
        setIsLoadingAddMission(false);
        console.error('Error creating mission:', err);
        const companyError = getMissionCompanyErrorMessage(language, err);
        if (companyError) {
          toast.error(companyError);
          return;
        }
        // The chosen perimeter was archived or deleted meanwhile: the list is
        // read again and the admin is taken back to the field to choose again.
        const perimeterError = getMissionPerimeterErrorMessage(language, err);
        if (perimeterError) {
          toast.error(perimeterError);
          perimeterField.reload();
          goToStep('details');
        }
      }
    );
  };

  const templateName = getTemplateTitle(
    openedTemplate || dataCreateMission.missionTemplate
  );

  const openTemplate = (template) => {
    setOpenedTemplate(template);
    setScreen('detail');
  };

  const useTemplate = () => {
    setDataCreateMission((prev) => ({
      ...prev,
      ...buildMissionDataFromTemplate(openedTemplate),
    }));
    setPage(1);
    setScreen('use');
  };

  const deleteTemplate = () => {
    if (!openedTemplate?._id) return;
    setIsDeletingTemplate(true);
    ApiDataBase.deleteAdminMissionTemplate(
      { token, id: openedTemplate._id },
      () => {
        setIsDeletingTemplate(false);
        toast.success('La fiche de poste a bien été supprimée.');
        setOpenedTemplate(null);
        setDataCreateMission((prev) => ({ ...prev, missionTemplate: null }));
        setScreen('select');
      },
      (err) => {
        setIsDeletingTemplate(false);
        console.error('Error deleting mission template:', err);
        toast.error('Suppression impossible');
      }
    );
  };

  const duplicateTemplate = () => {
    if (!openedTemplate?._id) return;
    setPendingDuplicateMissionTemplate({
      template: openedTemplate,
      siblings: companyTemplates,
    });
    toggleModal('mission_template_duplicate');
  };

  const createTemplate = () => {
    setTemplateForm(EMPTY_TEMPLATE_FORM);
    setTemplateStepKey(TEMPLATE_STEPS[0].key);
    setScreen('create');
  };

  const editTemplate = () => {
    setTemplateForm(templateToFormData(openedTemplate));
    setTemplateStepKey(TEMPLATE_STEPS[0].key);
    setScreen('edit');
  };

  // A template only conflicts with the others, never with itself.
  const editedTemplateId = screen === 'edit' ? openedTemplate?._id : null;

  const templateStepIndex = TEMPLATE_STEPS.findIndex(
    (step) => step.key === templateStepKey
  );
  const isLastTemplateStep = templateStepIndex === TEMPLATE_STEPS.length - 1;

  // A step is only left once what it asks for is filled.
  const goToTemplateStep = (key) => {
    const target = TEMPLATE_STEPS.findIndex((step) => step.key === key);
    if (target > templateStepIndex) {
      for (let index = templateStepIndex; index < target; index += 1) {
        const error = findTemplateStepError(
          TEMPLATE_STEPS[index].key,
          templateForm,
          companyTemplates,
          editedTemplateId
        );
        if (error) {
          toast.error(error);
          setTemplateStepKey(TEMPLATE_STEPS[index].key);
          return;
        }
      }
    }
    setTemplateStepKey(key);
  };

  // The API answers a save with the raw document, whose location is still an
  // id: the detail screen needs the read shape, so the template is re-read.
  const openSavedTemplate = (id) =>
    ApiDataBase.getAdminMissionTemplate(
      { token, id },
      (template) => {
        setOpenedTemplate(template);
        setIsSavingTemplate(false);
        setScreen('detail');
      },
      (err) => {
        console.error(err);
        setIsSavingTemplate(false);
        setScreen('select');
      }
    );

  // The api answers in English; only this one is shown to the admin translated.
  // The duplication modal lives outside this panel: it drops the id of the copy
  // in the context, and the panel opens it as it would after a creation.
  useEffect(() => {
    if (!duplicatedMissionTemplateId) return;
    setIsSavingTemplate(true);
    openSavedTemplate(duplicatedMissionTemplateId);
    setDuplicatedMissionTemplateId(null);
  }, [duplicatedMissionTemplateId]); // eslint-disable-line

  const onSaveTemplateError = (err, fallbackMessage) => {
    console.error(err);
    setIsSavingTemplate(false);
    const apiMessage = err?.response?.data?.message;
    if (apiMessage === TEMPLATE_NAME_TAKEN_API_MESSAGE) {
      toast.error(TEMPLATE_NAME_TAKEN_MESSAGE);
      setTemplateStepKey(TEMPLATE_STEPS[0].key);
      return;
    }
    toast.error(apiMessage || fallbackMessage);
  };

  const saveTemplate = () => {
    const error = findTemplateFormError(
      templateForm,
      companyTemplates,
      editedTemplateId
    );
    if (error) {
      toast.error(error.message);
      setTemplateStepKey(error.stepKey);
      return;
    }
    // A pending task counts as kept until its answer: once every check is
    // back, the save runs again and may still stop on the tasks step.
    if (hasPendingTasks(templateForm)) {
      setIsWaitingForTaskChecks(true);
      return;
    }
    setIsSavingTemplate(true);
    const data = buildTemplatePayload(templateForm);
    if (screen === 'edit') {
      ApiDataBase.patchAdminMissionTemplate(
        { token, id: openedTemplate?._id, data },
        (saved) => {
          toast.success('La fiche de poste a bien été modifiée.');
          openSavedTemplate(saved?._id || openedTemplate?._id);
        },
        (err) => onSaveTemplateError(err, 'Modification impossible')
      );
      return;
    }
    ApiDataBase.postAdminMissionTemplate(
      { token, idCompany: dataCreateMission.company, data },
      (saved) => {
        toast.success('La fiche de poste a bien été créée.');
        openSavedTemplate(saved?._id);
      },
      (err) => onSaveTemplateError(err, 'Création impossible')
    );
  };

  useEffect(() => {
    if (!isWaitingForTaskChecks || hasPendingTasks(templateForm)) return;
    setIsWaitingForTaskChecks(false);
    saveTemplate();
  }, [isWaitingForTaskChecks, templateForm]); // eslint-disable-line

  // Leaving the last step or the wizard calls off a save still waiting.
  useEffect(() => {
    setIsWaitingForTaskChecks(false);
  }, [templateStepKey, screen]);

  if (screen === 'create' || screen === 'edit') {
    const leaveWizard = () =>
      setScreen(screen === 'edit' ? 'detail' : 'select');
    const previousStep = () => {
      if (templateStepIndex <= 0) return leaveWizard();
      return setTemplateStepKey(TEMPLATE_STEPS[templateStepIndex - 1].key);
    };
    const nextStep = () =>
      goToTemplateStep(TEMPLATE_STEPS[templateStepIndex + 1].key);
    // Nothing to click on as long as the step is missing something.
    const stepError = findTemplateStepError(
      templateStepKey,
      templateForm,
      companyTemplates,
      editedTemplateId
    );
    return (
      <>
        {/* No cross: the wizard is left by its back link, never by a click that
            would drop what has been filled in. */}
        <SlideOverHeader hide={closeSlideOver} hideShow={false}>
          <div className="flex flex-col gap-4">
            <TemplateScreenTitle
              overline={dataCreateMission.companyName}
              title={templateText(
                screen === 'edit' ? 'editTitle' : 'createTitle'
              )}
              onBack={leaveWizard}
              backLabel="Revenir aux fiches de poste"
            />
            <TemplateStepBadges
              steps={TEMPLATE_STEPS}
              currentKey={templateStepKey}
              onSelect={goToTemplateStep}
            />
          </div>
        </SlideOverHeader>
        <SlideOverBody>
          <div className={'flex flex-col gap-6 text-sm'}>
            <TemplateForm
              stepKey={templateStepKey}
              form={templateForm}
              setForm={setTemplateForm}
              idCompany={dataCreateMission.company}
              idLocation={dataCreateMission.idLocation}
              onEditStep={setTemplateStepKey}
              siblingTemplates={companyTemplates}
              editedTemplateId={editedTemplateId}
            />
          </div>
        </SlideOverBody>
        <SlideOverFooter>
          <Button size="SM" color="SECONDARY" onClick={previousStep}>
            Retour
          </Button>
          {isLastTemplateStep ? (
            <Button
              size="SM"
              color={
                isSavingTemplate || isWaitingForTaskChecks
                  ? 'DISABLED'
                  : 'PRIMARY'
              }
              onClick={saveTemplate}
              disabled={isSavingTemplate || isWaitingForTaskChecks}
              aria-busy={isWaitingForTaskChecks ? 'true' : undefined}
            >
              {isWaitingForTaskChecks
                ? templateText('tasks.checkingBeforeSave')
                : templateText('saveButton')}
            </Button>
          ) : (
            /* The title carries the reason: a disabled button shows none. */
            <span title={stepError || undefined}>
              <Button
                size="SM"
                color={stepError ? 'DISABLED' : 'PRIMARY'}
                onClick={nextStep}
                disabled={!!stepError}
              >
                Suivant
              </Button>
            </span>
          )}
        </SlideOverFooter>
      </>
    );
  }

  if (screen === 'select' || screen === 'detail') {
    return (
      <>
        <SlideOverHeader
          hide={closeSlideOver}
          hideShow={screen === 'select'}
        >
          <TemplateScreenTitle
            overline={
              screen === 'detail'
                ? dataCreateMission.companyName
                : 'Fiche de poste'
            }
            title={
              screen === 'detail'
                ? templateName
                : 'Sélectionner une fiche de poste'
            }
            onBack={screen === 'detail' ? () => setScreen('select') : null}
            backLabel="Revenir aux fiches de poste"
          />
        </SlideOverHeader>
        <SlideOverBody>
          <div className={'flex flex-col gap-6 text-sm'}>
            {screen === 'select' ? (
              <TemplateSelectStep
                onOpenTemplate={openTemplate}
                onCreateTemplate={createTemplate}
                onTemplatesLoaded={setCompanyTemplates}
              />
            ) : (
              <TemplateDetail
                template={openedTemplate}
                riskyPositions={riskyPositions}
              />
            )}
          </div>
        </SlideOverBody>
        <SlideOverFooter>
          {screen === 'detail' ? (
            <>
              {/* mr-auto on the last one: Supprimer and Dupliquer sit on the
                  left, everything else is pushed to the right. */}
              <Button
                size="SM"
                color={isDeletingTemplate ? 'DISABLED' : 'SECONDARY'}
                onClick={deleteTemplate}
                disabled={isDeletingTemplate}
                aria-label="Supprimer la fiche de poste"
                title="Supprimer"
              >
                <TrashIcon wh={18} color="#B91C1C" />
              </Button>
              <Button
                size="SM"
                color="SECONDARY"
                className="mr-auto"
                onClick={duplicateTemplate}
                aria-label="Dupliquer la fiche de poste"
                title="Dupliquer"
              >
                <CopyIcon wh={18} />
              </Button>
              <Button
                size="SM"
                color="SECONDARY"
                onClick={editTemplate}
              >
                <span aria-hidden="true">
                  <Pencil wh={18} color="#374151" />
                </span>
                Modifier
              </Button>
              <Button
                size="SM"
                color={
                  openedTemplate?.isBelowMinimumWage ? 'DISABLED' : 'PRIMARY'
                }
                onClick={useTemplate}
                disabled={openedTemplate?.isBelowMinimumWage}
              >
                Utiliser
              </Button>
            </>
          ) : (
            <Button size={'SM'} color={'SECONDARY'} onClick={closeSlideOver}>
              Annuler
            </Button>
          )}
        </SlideOverFooter>
      </>
    );
  }

  return (
    <>
      {/* Same here: the mission being filled leaves by its back link. */}
      <SlideOverHeader hide={closeSlideOver} hideShow={false}>
        <div className="flex flex-col gap-4">
          <TemplateScreenTitle
            overline={dataCreateMission.companyName}
            title="Utiliser la fiche de poste"
            onBack={() => setScreen('select')}
            backLabel="Revenir aux fiches de poste"
          />
          <TemplateStepBadges
            steps={MISSION_STEPS}
            currentKey={stepKey}
            onSelect={goToStep}
          />
        </div>
      </SlideOverHeader>
      <SlideOverBody>
        <div className={'flex flex-col gap-6 text-sm'}>
          {stepKey === 'motif' && <SlideOverAddMissionMotif />}
          {stepKey === 'period' && <SlideOverAddMissionPlanning />}
          {stepKey === 'details' && (
            <TemplateMissionDetailsStep perimeterField={perimeterField} />
          )}
          {stepKey === 'invitation' && (
            <SlideOverAddMissionPlanningInvitationAttributionNotification />
          )}
          {stepKey === 'confirmation' && (
            <TemplateMissionConfirmation
              goToStep={goToStep}
              coefficientField={coefficientField}
            />
          )}
        </div>
      </SlideOverBody>
      <SlideOverFooter>
        <Button size={'SM'} color={'SECONDARY'} onClick={closeSlideOver}>
          Annuler
        </Button>
        {isLoadingAddMission ? (
          <Button size="SM" color="DISABLED">
            Création de la mission ...
          </Button>
        ) : (
          <>
            <Button
              size="SM"
              color="SECONDARY"
              onClick={() => {
                if (page === 1) {
                  setScreen('detail');
                  return;
                }
                setPage(page - 1);
              }}
            >
              Précédent
            </Button>
            {!isLastStep ? (
              <Button
                size="SM"
                color={canContinue ? 'PRIMARY' : 'DISABLED'}
                onClick={() => {
                  if (canContinue) {
                    setPage(page + 1);
                  }
                }}
                disabled={!canContinue}
              >
                Continuer
              </Button>
            ) : (
              <Button
                size="SM"
                color={coefficientField.isInvalid ? 'DISABLED' : 'PRIMARY'}
                onClick={submitAddMission}
                disabled={coefficientField.isInvalid}
              >
                Confirmer
              </Button>
            )}
          </>
        )}
      </SlideOverFooter>
    </>
  );
};

export default slideOverAddMission;
